/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Grid,
  Typography,
  makeStyles,
  Divider,
  ClickAwayListener,
  Tooltip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import StarIcon from '@material-ui/icons/Star';
import React from 'react';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { useHistory } from 'react-router';
import { Formik } from 'formik';
import { ChromePicker } from 'react-color';
import ColorLensIcons from '@material-ui/icons/ColorLens';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import i18n from 'i18n';
import Button from 'components/Button';
import { applyI18n } from 'utils/helpers';
import { CustomFields } from 'modules/partners/interfaces';
import {
  editCustomRequest,
  saveCustomRequest,
  removeImageCustomRequest,
} from 'modules/partners/actions';
import { connect } from 'react-redux';
import CheckIcon from '@material-ui/icons/Check';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import ApplicationState from 'store/interfaces';
import clsx from 'clsx';
import styles, { overrideModal } from './styles';
import { InitialValues } from './initiValues';
import schema from './schema';
import ScreenFields from './ScreenFields';
import TextFieldColor from './TextFieldColor';
import CustomImagePreview from './CustomImagePreview';

const useStyles = makeStyles(styles);

interface Props {
  id: string;
  open: boolean;
  customDetails: CustomFields | null;
  isLoading: boolean;
  editCustomRequest: ({ values, id }) => void;
  removeImageCustomRequest: ({ id, values }) => void;
  saveCustomRequest: ({ values, id }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

export const LOCAL_STORAGE_CUSTOM_KEY = '@AutoSky:customPreSave';

function TitleDivider({ children }) {
  const classes = useStyles();
  return (
    <Grid item xs={12}>
      <Typography variant="h6" component="h2" className={classes.titleDivider}>
        {children}
      </Typography>
    </Grid>
  );
}

const FormCustomFront = ({
  open,
  customDetails,
  editCustomRequest,
  saveCustomRequest,
  removeImageCustomRequest,
  modalClose,
  modalOpen,
  id,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [selector, setSelector] = React.useState(false);
  const [editSubdomain, setEditSubdomain] = React.useState(false);
  const [defaultBanner, setDefaultBanner] = React.useState(false);
  const [defaultBannerSelected, setDefaultBannerSelected] = React.useState(
    customDetails?.default_banner_option
  );
  const [isOpen, setIsOpen] = React.useState({
    open: false,
    key: '',
  });
  const [colors, setColors] = React.useState({
    hex: '',
    field: '',
  });

  const handleChange = (hex: string | any, field: string) => {
    setColors({
      hex,
      field,
    });
  };

  const imageRemoveList: string[] = [];

  const handleOpen = (open: boolean, key: string) => {
    setIsOpen({
      open,
      key,
    });
  };

  const isOpenColor = (key: string) => {
    return isOpen.open === true && isOpen.key === key;
  };

  const pickColor = (values: CustomFields, key: string) =>
    colors.field === key ? colors.hex : values?.[key];

  const handleOpenImage = (imageURL: string, description?: string) => {
    modalOpen({
      maxWidth: 'xl',
      overrideClasses: overrideModal,
      title: i18n.t('PARTNER_PAGE.USE_EXAMPLE_KEY'),
      content: (
        <>
          {description && (
            <Typography className={classes.description}>
              {i18n.t(description)}
            </Typography>
          )}
          <img src={imageURL} height={600} alt="example tooltip" />
        </>
      ),
      genericFooter: {
        action: () => {
          modalClose();
        },
        label: i18n.t('CLOSE_KEY'),
      },
    });
  };

  const handleClose = (key: string) => {
    if (isOpen.open === true && isOpen.key === key) {
      setIsOpen({
        key: '',
        open: false,
      });
    }
  };

  const removeImagesUndefinedFields = (values: CustomFields) => {
    const temp = { ...values };
    Object.keys(temp).forEach(key => {
      if (key === 'is_full_domain') return;
      if (!temp[key]) {
        delete temp[key];
      }
    });
    return temp;
  };

  const handlePublicCustom = (values: CustomFields) => {
    const newValues: any = [
      'background_image',
      'logo_image',
      'maintenance_image',
      'messages_image',
      'favicon_image',
    ].reduce((acc, item) => {
      return {
        ...acc,
        [item]: typeof values[item] === 'string' ? undefined : values[item],
      };
    }, values);

    modalOpen({
      title: i18n.t('PARTNER_PAGE.PUBLIC_CUSTOM_KEY'),
      content: i18n.t('PARTNER_PAGE.PUBLIC_CUSTOM_MSG_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              const requestPayload = {
                values: removeImagesUndefinedFields(newValues),
                id,
              };

              const executeRequests = () => {
                if (imageRemoveList.length > 0) {
                  removeImageCustomRequest({
                    id,
                    values: imageRemoveList,
                  });
                }
              };

              if (Object.keys(customDetails || {}).length > 0) {
                editCustomRequest(requestPayload);
              } else {
                saveCustomRequest(requestPayload);
              }

              executeRequests();
              modalClose();
            }}
          >
            {i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  React.useEffect(() => {
    setIsOpen({
      key: '',
      open: false,
    });
  }, [open]);

  const handleRemoveImage = (idToRemove: string) => {
    return imageRemoveList.includes(idToRemove)
      ? imageRemoveList.filter(item => item !== idToRemove)
      : imageRemoveList.push(idToRemove);
  };

  const mergedValues = {
    ...InitialValues,
    ...Object.fromEntries(
      Object.entries(customDetails || {}).map(([key, value]) => [
        key,
        value === null ? InitialValues[key] : value,
      ])
    ),
    is_full_domain:
      (customDetails as any)?.is_full_domain ??
      (InitialValues as any)?.is_full_domain ??
      false,
  };

  const FormSection = ({
    screenFieldIndices,
    titleImage,
    subtitleImage,
    values,
    setFieldValue,
    errors,
  }) => (
    <>
      {screenFieldIndices.map(([titleIndex, subtitleIndex], idx) => (
        <Grid
          item
          container
          xs={12}
          alignItems="center"
          key={idx}
          className={classes.configText}
        >
          <HelpOutlineIcon
            className={classes.helpIconImage}
            onClick={() => handleOpenImage(titleImage)}
          />
          <GenericForm
            fields={values}
            screenFields={applyI18n(
              ScreenFields.slice(titleIndex, titleIndex + 1),
              i18n.language
            )}
            setFieldValue={setFieldValue}
            errors={errors}
          />
          <HelpOutlineIcon
            className={classes.helpIconImageRight}
            onClick={() => handleOpenImage(subtitleImage)}
          />
          <GenericForm
            fields={values}
            screenFields={applyI18n(
              ScreenFields.slice(subtitleIndex, subtitleIndex + 1),
              i18n.language
            )}
            setFieldValue={setFieldValue}
            errors={errors}
          />
        </Grid>
      ))}
    </>
  );

  const fontsAndTextsLoginSection = [
    {
      screenFieldIndices: [
        [17, 18],
        [19, 20],
        [21, 22],
      ],
      titleImage: '/images/customExamples/title_font.png',
      subtitleImage: '/images/customExamples/subtitle_font.png',
    },
  ];

  const fontsAndTextsMFASection = [
    {
      screenFieldIndices: [
        [25, 26],
        [27, 28],
        [29, 30],
      ],
      titleImage: '/images/customExamples/title_mfa.png',
      subtitleImage: '/images/customExamples/subtitle_mfa.png',
    },
  ];

  return (
    <Grid item xs={12}>
      <Formik
        initialValues={mergedValues}
        validateOnChange
        validateOnBlur
        validationSchema={schema}
        onSubmit={() => {}}
      >
        {({ setFieldValue, errors, values }) => (
          <>
            <Grid item container xs={12} spacing={2} alignItems="center">
              <Divider className={classes.divider} />
              <TitleDivider>{i18n.t('CONFIG_KEY')}</TitleDivider>
              <Grid item container xs={12} alignItems="center">
                <Grid item xs={12} style={{ marginBottom: 8 }}>
                  <FormControl
                    component="fieldset"
                    className={classes.domainTypeRow}
                  >
                    <Typography className={classes.formLabelInline}>
                      {i18n.t('PARTNER_PAGE.DOMAIN_TYPE_KEY')}:
                    </Typography>

                    <RadioGroup
                      row
                      name="domain-type"
                      value={values.is_full_domain ? 'full' : 'sub'}
                      onChange={e =>
                        setFieldValue(
                          'is_full_domain',
                          e.target.value === 'full'
                        )
                      }
                    >
                      <FormControlLabel
                        value="sub"
                        control={<Radio size="small" />}
                        label={i18n.t('PARTNER_PAGE.SUBDOMAIN_OPTION_KEY')}
                        classes={{
                          root: classes.formControlLabel,
                          label: classes.radioLabel,
                        }}
                      />
                      <FormControlLabel
                        value="full"
                        control={<Radio size="small" />}
                        label={i18n.t('PARTNER_PAGE.FULL_DOMAIN_OPTION_KEY')}
                        classes={{
                          root: classes.formControlLabel,
                          label: classes.radioLabel,
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      values.is_full_domain
                        ? '/images/customExamples/full_domain.png'
                        : '/images/customExamples/subdomain.png'
                    )
                  }
                />

                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 5,
                      label: values.is_full_domain
                        ? i18n.t('PARTNER_PAGE.FULL_DOMAIN_KEY')
                        : i18n.t('PARTNER_PAGE.SUBDOMAIN_KEY'),
                      stateKey: 'domain',
                      disabled: !!customDetails?.domain && !editSubdomain,
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                {!!customDetails?.domain && (
                  <Tooltip arrow title={i18n.t('EDIT_KEY')}>
                    <EditIcon
                      className={classes.editIcon}
                      onClick={() => setEditSubdomain(!editSubdomain)}
                    />
                  </Tooltip>
                )}
                <HelpOutlineIcon
                  className={classes.helpIconImageRight}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/title.png')
                  }
                />
                <GenericForm
                  fields={values}
                  screenFields={applyI18n(
                    ScreenFields.slice(1, 2),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              </Grid>

              <TitleDivider>{i18n.t('COLORS_KEY')}</TitleDivider>
              <Grid container className={classes.colorGrid}>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/primary_main.png')
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values?.primary_main_color}
                  label={i18n.t('PARTNER_PAGE.HIGHLIGHT_COLOR_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="primary_main_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('primary_main_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'primary_main_color')
                      }
                    />

                    {!!isOpenColor('primary_main_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'primary_main_color')}
                          onChange={value => {
                            handleChange(value.hex, 'primary_main_color');
                            setFieldValue('primary_main_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/environment_text_color.png'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values?.primary_contrast_text_color}
                  label={i18n.t('PARTNER_PAGE.ENVIRONMENT_TITLE_COLOR_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="primary_contrast_text_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('primary_contrast_text_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'primary_contrast_text_color')
                      }
                    />

                    {!!isOpenColor('primary_contrast_text_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(
                            values,
                            'primary_contrast_text_color'
                          )}
                          onChange={value => {
                            handleChange(
                              value.hex,
                              'primary_contrast_text_color'
                            );
                            setFieldValue(
                              'primary_contrast_text_color',
                              value.hex
                            );
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
              </Grid>
              <Grid container className={classes.colorGrid}>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/background_color.png'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values.background_color}
                  label={i18n.t('PARTNER_PAGE.LOGIN_BUTTONS_COLORS_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="background_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('background_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'background_color')
                      }
                    />
                    {!!isOpenColor('background_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'background_color')}
                          onChange={value => {
                            handleChange(value.hex, 'background_color');
                            setFieldValue('background_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>

                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/background_image_color.png',
                      'PARTNER_PAGE.COLORS_BANNER_IMAGE_HELP_TEXT_KEY'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values.image_login_background_color}
                  label={i18n.t('PARTNER_PAGE.COLORS_BANNER_IMAGE')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="image_login_background_color"
                />
                <ClickAwayListener
                  onClickAway={() =>
                    handleClose('image_login_background_color')
                  }
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'image_login_background_color')
                      }
                    />

                    {!!isOpenColor('image_login_background_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(
                            values,
                            'image_login_background_color'
                          )}
                          onChange={value => {
                            handleChange(
                              value.hex,
                              'image_login_background_color'
                            );
                            setFieldValue(
                              'image_login_background_color',
                              value.hex
                            );
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
              </Grid>
              <Grid container className={classes.colorGrid}>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/url_details.png')
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values.message_text_color}
                  label={i18n.t('PARTNER_PAGE.MSG_COLORS_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="message_text_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('message_text_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'message_text_color')
                      }
                    />
                    {!!isOpenColor('message_text_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'message_text_color')}
                          onChange={value => {
                            handleChange(value.hex, 'message_text_color');
                            setFieldValue('message_text_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>

                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/environment_color_hover_text.png',
                      'PARTNER_PAGE.TEXT_HOVER_COLOR_HELP_TEXT_KEY'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values.text_hover_color}
                  label={i18n.t('PARTNER_PAGE.COLOR_HOVER_TEXT_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="text_hover_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('text_hover_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'text_hover_color')
                      }
                    />
                    {!!isOpenColor('text_hover_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'text_hover_color')}
                          onChange={value => {
                            handleChange(value.hex, 'text_hover_color');
                            setFieldValue('text_hover_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
              </Grid>
              <Grid container className={classes.colorGrid}>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/virtualizer_choice.png'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values?.secondary_main_color}
                  label={i18n.t('PARTNER_PAGE.MESSAGES_BUTTON_COLOR_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="secondary_main_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('secondary_main_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'secondary_main_color')
                      }
                    />

                    {!!isOpenColor('secondary_main_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'secondary_main_color')}
                          onChange={value => {
                            handleChange(value.hex, 'secondary_main_color');
                            setFieldValue('secondary_main_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/logo_background_color.png'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values?.logo_background_color}
                  label={i18n.t('PARTNER_PAGE.LOGO_BACKGROUND_COLOR_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="logo_background_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('logo_background_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'logo_background_color')
                      }
                    />

                    {!!isOpenColor('logo_background_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'logo_background_color')}
                          onChange={value => {
                            handleChange(value?.hex, 'logo_background_color');
                            setFieldValue('logo_background_color', value?.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
              </Grid>

              <Grid container className={classes.colorGrid}>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/details_login_image_example.png'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values?.details_login_color}
                  label={i18n.t('CUSTOM_DETAILS_COLOR_DETAILS_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="details_login_color"
                />
                <ClickAwayListener
                  onClickAway={() => handleClose('details_login_color')}
                >
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() =>
                        handleOpen(!isOpen.open, 'details_login_color')
                      }
                    />

                    {!!isOpenColor('details_login_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'details_login_color')}
                          onChange={value => {
                            handleChange(value.hex, 'details_login_color');
                            setFieldValue('details_login_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage(
                      '/images/customExamples/app_color_example.png'
                    )
                  }
                />
                <TextFieldColor
                  errors={errors}
                  value={values?.app_color}
                  label={i18n.t('CUSTOM_APP_COLOR_DETAILS_KEY')}
                  setFieldValue={(a, b) => {
                    setFieldValue(a, b);
                    handleChange(b, a);
                  }}
                  stateKey="app_color"
                />
                <ClickAwayListener onClickAway={() => handleClose('app_color')}>
                  <Grid>
                    <ColorLensIcons
                      className={classes.icon}
                      onClick={() => handleOpen(!isOpen.open, 'app_color')}
                    />

                    {!!isOpenColor('app_color') && (
                      <div className={classes.colorContainer}>
                        <ChromePicker
                          color={pickColor(values, 'app_color')}
                          onChange={value => {
                            handleChange(value.hex, 'app_color');
                            setFieldValue('app_color', value.hex);
                          }}
                          className={classes.colorContent}
                        />
                      </div>
                    )}
                  </Grid>
                </ClickAwayListener>
              </Grid>
              <TitleDivider>{i18n.t('FONTS_KEY')}</TitleDivider>
              <Grid item container xs={12} alignItems="center">
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/title_font.png')
                  }
                />
                <GenericForm
                  fields={values}
                  screenFields={applyI18n(
                    ScreenFields.slice(15, 16),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <HelpOutlineIcon
                  className={classes.helpIconImageRight}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/subtitle_font.png')
                  }
                />
                <GenericForm
                  fields={values}
                  screenFields={applyI18n(
                    ScreenFields.slice(16, 17),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              </Grid>

              {fontsAndTextsLoginSection.map((section, index) => (
                <FormSection
                  key={index}
                  {...section}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              ))}
              <TitleDivider>{i18n.t('TITLE_FONTS_MFA_KEY')}</TitleDivider>
              <Grid item container xs={12} alignItems="center">
                <HelpOutlineIcon
                  className={classes.helpIconImage}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/title_mfa.png')
                  }
                />
                <GenericForm
                  fields={values}
                  screenFields={applyI18n(
                    ScreenFields.slice(23, 24),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <HelpOutlineIcon
                  className={classes.helpIconImageRight}
                  onClick={() =>
                    handleOpenImage('/images/customExamples/subtitle_mfa.png')
                  }
                />
                <GenericForm
                  fields={values}
                  screenFields={applyI18n(
                    ScreenFields.slice(24, 25),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              </Grid>

              {fontsAndTextsMFASection.map((section, index) => (
                <FormSection
                  key={index}
                  {...section}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              ))}
            </Grid>
            <Divider className={classes.divider} />
            <Grid container alignItems="center" justify="space-between">
              <Grid item xs={6}>
                <TitleDivider>{`${i18n.t('IMAGES_KEY')} (${i18n.t(
                  'JPEG_PNG_KEY'
                )})`}</TitleDivider>
              </Grid>
              <Grid item xs={5} className={classes.removeImagesGrid}>
                <Button
                  startIcon={!selector && <DeleteIcon />}
                  id="delete-images"
                  variant="outlined"
                  onClick={() => setSelector(!selector)}
                  deleteButton
                  className={classes.removeImagesButton}
                >
                  {!selector
                    ? i18n.t('PARTNER_PAGE.RESET_IMAGES_KEY')
                    : i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>

            <Divider className={classes.diverTitle} />
            <Grid
              container
              item
              xs={12}
              spacing={2}
              className={classes.imageDrop}
            >
              <CustomImagePreview
                imageId="background_image"
                label="PARTNER_PAGE.IMAGE_LOGIN_BANNER_KEY"
                selector={selector}
                values={values}
                handleOpenImage={handleOpenImage}
                handleRemoveImage={handleRemoveImage}
                setFieldValue={setFieldValue}
                additionalButton={
                  <Button
                    endIcon={
                      defaultBannerSelected !== 0 &&
                      defaultBannerSelected !== null && <CheckIcon />
                    }
                    variant="outlined"
                    className={clsx(classes.defaultImageButton, {
                      [classes.defaultImageButtonActive]:
                        defaultBannerSelected !== 0 &&
                        defaultBannerSelected !== null,
                    })}
                    onClick={e => {
                      e.stopPropagation();
                      setDefaultBanner(!defaultBanner);
                    }}
                  >
                    {i18n.t('CHOOSE_DEFAULT_IMAGE_KEY')}
                  </Button>
                }
              />

              <CustomImagePreview
                imageId="logo_image"
                label="PARTNER_PAGE.LOGO_PARTNER_KEY"
                selector={selector}
                values={values}
                handleOpenImage={handleOpenImage}
                handleRemoveImage={handleRemoveImage}
                setFieldValue={setFieldValue}
              />

              <CustomImagePreview
                imageId="messages_image"
                label="PARTNER_PAGE.MESSAGE_IMAGES_KEY"
                helperDescripton="PARTNER_PAGE.MESSAGES_IMAGE_HELP_TEXT_KEY"
                selector={selector}
                values={values}
                handleOpenImage={handleOpenImage}
                handleRemoveImage={handleRemoveImage}
                setFieldValue={setFieldValue}
              />

              <CustomImagePreview
                imageId="favicon_image"
                label="PARTNER_PAGE.FAVICON_KEY"
                selector={selector}
                values={values}
                handleOpenImage={handleOpenImage}
                handleRemoveImage={handleRemoveImage}
                setFieldValue={setFieldValue}
              />

              {defaultBanner && (
                <Grid container className={classes.imagesOptionsGrid}>
                  {Array.from({ length: 15 }, (key, index) => {
                    const bannerNumber = index + 1;
                    return (
                      <Grid
                        key={bannerNumber}
                        item
                        xs={4}
                        className={clsx(classes.defaultImageItem, {
                          [classes.cardSelectorActive]:
                            defaultBannerSelected === bannerNumber,
                        })}
                        onClick={() => {
                          if (
                            defaultBannerSelected === 0 ||
                            defaultBannerSelected === null
                          ) {
                            setDefaultBannerSelected(bannerNumber);
                            setFieldValue(
                              'default_banner_option',
                              bannerNumber
                            );
                          } else if (defaultBannerSelected === bannerNumber) {
                            setDefaultBannerSelected(0);
                            setFieldValue('default_banner_option', 0);
                          } else {
                            setDefaultBannerSelected(bannerNumber);
                            setFieldValue(
                              'default_banner_option',
                              bannerNumber
                            );
                          }
                        }}
                      >
                        <Grid item className={classes.iconGrid}>
                          <StarIcon
                            className={clsx(classes.checkIconModal, {
                              [classes.checkIconModalHidden]:
                                defaultBannerSelected !== bannerNumber,
                            })}
                          />
                        </Grid>
                        <img
                          width="100%"
                          height="90%"
                          src={`/images/front-customizado/defaultImage${bannerNumber}.png`}
                          alt={`Default Banner ${bannerNumber}`}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              <Grid container justify="center" className={classes.buttons}>
                <Button
                  id="submit-view-page"
                  onClick={() => {
                    handlePublicCustom(values);
                  }}
                  submitButton
                  disabled={!values.domain || Object.keys(errors).length !== 0}
                >
                  {i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_KEY')}
                </Button>
                {customDetails?.domain && (
                  <Button
                    startIcon={<ExitToAppIcon />}
                    id="access-button"
                    variant="outlined"
                    onClick={() => {
                      const { domain } = customDetails;
                      const isFull = (customDetails as any)?.is_full_domain;

                      const url = isFull
                        ? `https://${domain}`
                        : `https://${domain}.autosky.cloud`;

                      window.open(url, '_blank', 'noopener');
                    }}
                    className={classes.accessAccount}
                  >
                    {i18n.t('ACCESS_KEY')}
                  </Button>
                )}

                <Button
                  onClick={() => history.push('/all-partners')}
                  id="cancel-button"
                  variant="outlined"
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Formik>
    </Grid>
  );
};

const mapStateToProps = ({ partners: { isLoading } }: ApplicationState) => ({
  isLoading,
});

export default connect(mapStateToProps, {
  editCustomRequest,
  saveCustomRequest,
  removeImageCustomRequest,
  modalClose,
  modalOpen,
})(FormCustomFront);
